import { IonIcon, IonSpinner } from '@ionic/react'
import { flash, leaf, logoEuro } from 'ionicons/icons'
import React, { useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import LinkCard from '../../../../components/LinkCard'
import TimeScroller from '../../../../components/timeScroller'
import { co2PerKwh } from '../../../../constants'
import { AppContext } from '../../../../contexts/AppContext'
import { ChartTimeHook } from '../../../electricity/chartUtils/hooks/useChartTime'
import ChartTimeSwitch from '../../../electricity/components/ChartTimeSwitch'
import { createSolarPanelChart } from './createSolarPanelChart'

interface ColumnProps {
	icon: string
	value: number
	unit: string
	description: string
}

const Column: React.FC<ColumnProps> = ({ icon, value, unit, description }) => {
	return (
		<div className='column'>
			<IonIcon
				icon={icon}
				className='text-brand-primary-100-light dark:text-brand-primary-100-dark'
			/>
			<div className='value text-text-dark dark:text-white'>
				{value.toFixed(2)}{' '}
				<span className='unit text-text-grey-200 dark:text-text-grey-100'>{unit}</span>
			</div>
			<div className='desc text-text-grey-200 dark:text-text-grey-100'>{description}</div>
		</div>
	)
}

interface Props {
	time: ChartTimeHook
	chartData: number[]
	moneyData: number
	isLoading: boolean
}

const SolarPanelChart = ({ time, chartData, moneyData, isLoading }: Props) => {
	const { isDarkMode } = useContext(AppContext)
	const chartElement = useRef<HTMLCanvasElement>(null)
	const chartInstance = useRef<any>(null)

	const history = useHistory()

	const { t } = useTranslation('pages_solarPanelDetail')

	useEffect(() => {
		if (chartElement.current) {
			if (chartInstance.current) {
				chartInstance.current.destroy()
			}

			chartInstance.current = createSolarPanelChart(
				chartElement.current,
				chartData,
				time,
				isDarkMode
			)
		}

		return () => {
			if (chartInstance.current) {
				chartInstance.current.destroy()
			}
		}
	}, [chartData, isDarkMode])

	return (
		<>
			<div className='border bg-white dark:bg-brand-secondary-200-dark border-[#e5e5e5] dark:border-surface-dark rounded-[16px] relative overflow-hidden'>
				<div className='rounded-b-2xl z-30 relative dark:bg-brand-secondary-200-dark'>
					<TimeScroller time={time} mode='production' />
				</div>
				<div className='chart z-20 bg-white dark:bg-background-200-dark relative !pt-4 -top-4'>
					<div className={isLoading ? 'loading canvas-container' : 'canvas-container'}>
						{isLoading ? <IonSpinner /> : <canvas ref={chartElement} />}
					</div>
					<div className='consumption-element-detail-info'>
						<Column
							icon={flash}
							value={chartData.reduce((acc, sum) => acc + sum, 0)}
							unit={'kWh'}
							description={t('production')}
						/>
						<Column icon={logoEuro} value={moneyData} unit={'€'} description={t('saving')} />
						<Column
							icon={leaf}
							value={chartData.reduce((acc, sum) => acc + sum, 0) * co2PerKwh * -1}
							unit={'kg'}
							description={t('co2Saving')}
						/>
					</div>
				</div>

				<div className='z-10 relative bg-brand-primary-200-light dark:bg-brand-secondary-200-dark -mt-10'>
					<ChartTimeSwitch time={time} />
				</div>
			</div>
			<div id='consumption-element-detail' className='my-4'>
				<LinkCard
					title={t('settings')}
					subText={t('settingsText')}
					clickHandler={() => {
						history.push('/devices/solar-panel-settings')
					}}
				>
					<div className='counter bg-brand-primary-200-light text-brand-secondary-100-light dark:bg-brand-secondary-100-dark dark:text-brand-primary-200-dark'>
						1
					</div>
				</LinkCard>
			</div>
		</>
	)
}

export default SolarPanelChart
